/**
 * usePullToRefresh Hook
 *
 * A custom hook that integrates pull-to-refresh functionality with React Query.
 * Provides a unified way to handle refresh state and trigger data refetches.
 *
 * Features:
 * - Integrates with TanStack Query's refetch mechanism
 * - Tracks refreshing state independently
 * - Supports multiple queries being refreshed simultaneously
 * - Provides haptic feedback on supported devices
 *
 * Usage:
 * ```tsx
 * function MyList() {
 *   const { data, refetch, isFetching } = useMyData();
 *   const { isRefreshing, handleRefresh } = usePullToRefresh({
 *     onRefresh: refetch,
 *     isFetching,
 *   });
 *
 *   return (
 *     <PullToRefresh onRefresh={handleRefresh} isRefreshing={isRefreshing}>
 *       <MyContent data={data} />
 *     </PullToRefresh>
 *   );
 * }
 * ```
 */

import { useState, useCallback, useRef } from "react";

// ============================================================================
// Types
// ============================================================================

export interface UsePullToRefreshOptions {
  /**
   * Callback function to trigger data refresh.
   * Can be a React Query refetch function or any async function.
   */
  onRefresh: () => Promise<unknown> | unknown;

  /**
   * Whether data is currently being fetched.
   * Used to show loading state during background refetches.
   */
  isFetching?: boolean;

  /**
   * Minimum duration to show the refreshing state (in ms).
   * Helps prevent jarring UI when refresh completes very quickly.
   * Default: 500ms
   */
  minRefreshDuration?: number;

  /**
   * Whether to enable haptic feedback on supported devices.
   * Default: true
   */
  enableHapticFeedback?: boolean;
}

export interface UsePullToRefreshReturn {
  /** Whether a refresh is currently in progress */
  isRefreshing: boolean;

  /**
   * Handler function to pass to PullToRefresh component.
   * Manages the refresh lifecycle including minimum duration.
   */
  handleRefresh: () => Promise<void>;

  /**
   * Whether any data fetching is happening (refresh or background).
   * Useful for showing loading indicators.
   */
  isLoading: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_MIN_REFRESH_DURATION = 500;

// ============================================================================
// Haptic Feedback Utility
// ============================================================================

/**
 * Trigger haptic feedback on supported devices
 */
function triggerHapticFeedback() {
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    try {
      // Short vibration pattern for feedback
      navigator.vibrate(10);
    } catch {
      // Silently fail if vibration is not supported
    }
  }
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function usePullToRefresh({
  onRefresh,
  isFetching = false,
  minRefreshDuration = DEFAULT_MIN_REFRESH_DURATION,
  enableHapticFeedback = true,
}: UsePullToRefreshOptions): UsePullToRefreshReturn {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshStartTimeRef = useRef<number | null>(null);

  const handleRefresh = useCallback(async () => {
    // Prevent multiple concurrent refresh calls
    if (isRefreshing) return;

    // Track start time for minimum duration calculation
    refreshStartTimeRef.current = Date.now();
    setIsRefreshing(true);

    // Trigger haptic feedback
    if (enableHapticFeedback) {
      triggerHapticFeedback();
    }

    try {
      // Execute the refresh callback
      await onRefresh();
    } catch (error) {
      // Log error but don't throw - let the UI handle error states
      console.error("Pull to refresh error:", error);
    } finally {
      // Calculate remaining time to meet minimum duration
      const elapsed = Date.now() - (refreshStartTimeRef.current ?? 0);
      const remainingTime = Math.max(0, minRefreshDuration - elapsed);

      // Wait for minimum duration to complete
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      setIsRefreshing(false);
      refreshStartTimeRef.current = null;
    }
  }, [onRefresh, isRefreshing, minRefreshDuration, enableHapticFeedback]);

  // Combined loading state
  const isLoading = isRefreshing || isFetching;

  return {
    isRefreshing,
    handleRefresh,
    isLoading,
  };
}

// ============================================================================
// Multi-Query Refresh Hook
// ============================================================================

export interface UseMultiQueryRefreshOptions {
  /**
   * Array of refetch functions from multiple React Query hooks.
   */
  refetchFunctions: Array<() => Promise<unknown>>;

  /**
   * Whether any of the queries is currently fetching.
   */
  isFetching?: boolean;

  /**
   * Minimum duration to show the refreshing state (in ms).
   * Default: 500ms
   */
  minRefreshDuration?: number;
}

/**
 * Hook for refreshing multiple queries simultaneously.
 * Useful for pages that display data from multiple sources.
 *
 * Usage:
 * ```tsx
 * const { refetch: refetchHome, isFetching: isFetchingHome } = useHomeData();
 * const { refetch: refetchHoldings, isFetching: isFetchingHoldings } = useHoldings();
 *
 * const { isRefreshing, handleRefresh } = useMultiQueryRefresh({
 *   refetchFunctions: [refetchHome, refetchHoldings],
 *   isFetching: isFetchingHome || isFetchingHoldings,
 * });
 * ```
 */
export function useMultiQueryRefresh({
  refetchFunctions,
  isFetching = false,
  minRefreshDuration = DEFAULT_MIN_REFRESH_DURATION,
}: UseMultiQueryRefreshOptions): UsePullToRefreshReturn {
  const combinedRefetch = useCallback(async () => {
    // Execute all refetch functions in parallel
    await Promise.all(refetchFunctions.map((fn) => fn()));
  }, [refetchFunctions]);

  return usePullToRefresh({
    onRefresh: combinedRefetch,
    isFetching,
    minRefreshDuration,
  });
}

// ============================================================================
// Export
// ============================================================================

export default usePullToRefresh;
